import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { salesApi, warrantyPlanApi, tenantApi, branchApi, prohibitedDevicesApi, uploadApi, phoneBrandsApi, formatApiErrorDetail, useAuth } from '../../lib/api';
import { formatCurrency, cn } from '../../lib/utils';
import { AppLayout } from '../../components/Layout';
import { AutoSuggest } from '../../components/AutoSuggest';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  ShoppingCart,
  User,
  Smartphone,
  ShieldCheck,
  CheckCircle,
  AlertTriangle,
  Loader2,
  ArrowRight,
  ArrowLeft,
} from 'lucide-react';
import { toast } from 'sonner';

export default function NewSalePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [tenant, setTenant] = useState(null);
  const [branches, setBranches] = useState([]);
  const [eligiblePlans, setEligiblePlans] = useState([]);
  const [showOverrideDialog, setShowOverrideDialog] = useState(false);
  const [success, setSuccess] = useState(null);
  const [prohibitedWarning, setProhibitedWarning] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [deviceBrands, setDeviceBrands] = useState([]);
  const [brandSearch, setBrandSearch] = useState('');
  const [branchSearch, setBranchSearch] = useState('');
  const [brandsData, setBrandsData] = useState([]);

  const isShopOwner = user?.role === 'SHOP_OWNER';

  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    device_brand: '',
    device_model: '',
    device_imei: '',
    device_price: '',
    warranty_attached: false,
    warranty_plan_id: '',
    warranty_price_override: '',
    warranty_override_reason: '',
    branch_id: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const promises = [tenantApi.get(user.tenant_id), phoneBrandsApi.list()];
        if (isShopOwner) promises.push(branchApi.list());
        const results = await Promise.all(promises);
        setTenant(results[0].data);
        setBrandsData(results[1].data);
        setDeviceBrands(results[1].data.map(b => b.name));
        if (isShopOwner && results[2]) {
          const bData = results[2].data;
          setBranches((bData.items || bData).filter(b => b.status === 'ACTIVE'));
        }
      } catch (error) {
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    if (user?.tenant_id) fetchData();
  }, [user, isShopOwner]);

  const fetchEligiblePlans = async (price, osType) => {
    try {
      const { data } = await warrantyPlanApi.getEligible(price, osType);
      setEligiblePlans(data);
      // Auto-select if only one plan
      if (data.length === 1) {
        setFormData(prev => ({ ...prev, warranty_plan_id: data[0].id }));
      }
    } catch (error) {
      setEligiblePlans([]);
    }
  };

  useEffect(() => {
    if (formData.device_price && parseFloat(formData.device_price) > 0) {
      const selectedBrand = brandsData.find(b => b.name === formData.device_brand);
      const osType = selectedBrand?.os_type || null;
      fetchEligiblePlans(parseFloat(formData.device_price), osType);
    } else {
      setEligiblePlans([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.device_price, formData.device_brand]);

  const validateStep1 = () => {
    if (isShopOwner && !formData.branch_id) {
      toast.error('Please select a branch');
      return false;
    }
    if (!formData.customer_name.trim()) {
      toast.error('Please enter customer name');
      return false;
    }
    if (!formData.customer_phone.trim() || formData.customer_phone.length < 10) {
      toast.error('Please enter a valid phone number');
      return false;
    }
    if (!formData.device_brand) {
      toast.error('Please select device brand');
      return false;
    }
    if (!formData.device_model.trim()) {
      toast.error('Please enter device model');
      return false;
    }
    if (!formData.device_imei || formData.device_imei.length !== 15) {
      toast.error('IMEI must be exactly 15 digits');
      return false;
    }
    if (!formData.device_price || parseFloat(formData.device_price) <= 0) {
      toast.error('Please enter a valid device price');
      return false;
    }
    return true;
  };

  // Client-side image compression
  const compressImage = (file, maxWidth = 1200, quality = 0.7) => {
    return new Promise((resolve) => {
      if (file.type === 'application/pdf') { resolve(file); return; }
      const img = new Image();
      const canvas = document.createElement('canvas');
      img.onload = () => {
        let w = img.width, h = img.height;
        if (w > maxWidth) { h = (maxWidth / w) * h; w = maxWidth; }
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        canvas.toBlob((blob) => resolve(new File([blob], file.name, { type: 'image/jpeg' })), 'image/jpeg', quality);
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const compressed = await compressImage(file);
        const { data } = await uploadApi.upload(compressed);
        setAttachments(prev => [...prev, { id: data.id, filename: data.filename, size: data.size }]);
      }
      toast.success(`${files.length} file(s) uploaded`);
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleNextStep = async () => {
    if (validateStep1()) {
      // Check if device is prohibited
      try {
        const { data } = await prohibitedDevicesApi.check(formData.device_brand, formData.device_model);
        if (data.is_prohibited) {
          setProhibitedWarning(data);
        } else {
          setProhibitedWarning(null);
        }
      } catch {
        setProhibitedWarning(null);
      }
      setStep(2);
    }
  };

  const handleSubmit = async () => {
    // Check for exclusive contract compliance
    const isExclusive = tenant?.contract_type === 'EXCLUSIVE';
    const hasWarranty = formData.warranty_attached && formData.warranty_plan_id;

    if (isExclusive && !hasWarranty) {
      if (!formData.warranty_override_reason || formData.warranty_override_reason.length < 20) {
        setShowOverrideDialog(true);
        return;
      }
    }

    setSubmitting(true);
    try {
      const { data } = await salesApi.create({
        customer_name: formData.customer_name,
        customer_phone: formData.customer_phone,
        customer_email: formData.customer_email || null,
        device_brand: formData.device_brand,
        device_model: formData.device_model,
        device_imei: formData.device_imei,
        device_price: parseFloat(formData.device_price),
        warranty_attached: formData.warranty_attached && !!formData.warranty_plan_id,
        warranty_plan_id: formData.warranty_attached ? formData.warranty_plan_id : null,
        warranty_price_override: formData.warranty_price_override ? parseFloat(formData.warranty_price_override) : null,
        warranty_override_reason: (!formData.warranty_attached && isExclusive) 
          ? formData.warranty_override_reason 
          : null,
        branch_id: isShopOwner ? formData.branch_id : null,
        attachments: attachments.map(a => a.id),
      });

      setSuccess(data);
      toast.success('Sale recorded successfully!');
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to record sale');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOverrideSubmit = () => {
    if (formData.warranty_override_reason.length < 20) {
      toast.error('Override reason must be at least 20 characters');
      return;
    }
    setShowOverrideDialog(false);
    handleSubmit();
  };

  const handleNewSale = () => {
    setFormData({
      customer_name: '',
      customer_phone: '',
      customer_email: '',
      device_brand: '',
      device_model: '',
      device_imei: '',
      device_price: '',
      warranty_attached: false,
      warranty_plan_id: '',
      warranty_override_reason: '',
      branch_id: '',
    });
    setStep(1);
    setAttachments([]);
    setSuccess(null);
    setEligiblePlans([]);
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#005DE8]" />
        </div>
      </AppLayout>
    );
  }

  // Success Screen
  if (success) {
    return (
      <AppLayout>
        <div className="max-w-lg mx-auto mt-8">
          <Card className="border-slate-200 text-center">
            <CardContent className="pt-8 pb-8">
              <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-emerald-500" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>
                Sale Recorded!
              </h2>
              <p className="text-slate-600 mb-6">
                The sale has been successfully recorded in the system.
              </p>

              {success.approval_status === 'PENDING' && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                  <p className="font-medium text-amber-800">Pending Approval</p>
                  <p className="text-sm text-amber-700">This sale is awaiting Shop Owner approval before warranty is activated.</p>
                </div>
              )}

              {success.warranty_certificate && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
                  <ShieldCheck className="h-8 w-8 text-[#005DE8] mx-auto mb-3" />
                  <p className="text-sm text-slate-600 mb-1">Warranty Certificate Number</p>
                  <p className="text-xl font-bold text-[#005DE8]">{success.warranty_certificate}</p>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={handleNewSale}
                  className="bg-[#005DE8] hover:bg-[#0047B3]"
                  data-testid="new-sale-button"
                >
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  New Sale
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate(isShopOwner ? '/dashboard/sales' : '/sales/history')}
                  data-testid="view-history-button"
                >
                  View Sales History
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  const isExclusive = tenant?.contract_type === 'EXCLUSIVE';
  const selectedPlan = eligiblePlans.find(p => p.id === formData.warranty_plan_id);

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
            New Sale
          </h1>
          <p className="text-slate-600 mt-1">Record a new mobile sale</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center gap-4 mb-8">
          <div className={cn(
            "flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium",
            step >= 1 ? "bg-[#005DE8] text-white" : "bg-slate-100 text-slate-500"
          )}>
            <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs">1</span>
            Device & Customer
          </div>
          <div className="flex-1 h-0.5 bg-slate-200" />
          <div className={cn(
            "flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium",
            step >= 2 ? "bg-[#005DE8] text-white" : "bg-slate-100 text-slate-500"
          )}>
            <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs">2</span>
            Warranty
          </div>
        </div>

        {/* Exclusive Contract Banner */}
        {isExclusive && (
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-purple-600 mt-0.5" />
            <div>
              <p className="font-medium text-purple-900">Exclusive Contract</p>
              <p className="text-sm text-purple-700">
                All sales must include a Mobikoo warranty unless you provide a valid exemption reason.
              </p>
            </div>
          </div>
        )}

        {/* Step 1: Device & Customer */}
        {step === 1 && (
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                <User className="h-5 w-5 text-[#005DE8]" />
                Customer & Device Details
              </CardTitle>
              <CardDescription>Enter the customer and device information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Branch Selection (Shop Owner only) */}
              {isShopOwner && (
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-3">Branch</h4>
                  <AutoSuggest
                    options={branches.map(b => ({ label: `${b.branch_name} - ${b.location}`, value: b.id }))}
                    value={formData.branch_id}
                    onChange={(value) => setFormData({ ...formData, branch_id: value })}
                    placeholder="Search branch..."
                    testId="sale-branch-select"
                  />
                </div>
              )}

              {/* Customer Info */}
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-3">Customer Information</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="customer_name">Customer Name *</Label>
                    <Input
                      id="customer_name"
                      value={formData.customer_name}
                      onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                      placeholder="John Doe"
                      required
                      data-testid="customer-name-input"
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="customer_phone">Phone Number *</Label>
                    <Input
                      id="customer_phone"
                      type="tel"
                      value={formData.customer_phone}
                      onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                      placeholder="9876543210"
                      required
                      data-testid="customer-phone-input"
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="customer_email">Email (Optional)</Label>
                    <Input
                      id="customer_email"
                      type="email"
                      value={formData.customer_email}
                      onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                      placeholder="john@example.com"
                      data-testid="customer-email-input"
                      className="h-11"
                    />
                  </div>
                </div>
              </div>

              {/* Device Info */}
              <div className="pt-4 border-t">
                <h4 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <Smartphone className="h-4 w-4" />
                  Device Information
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="device_brand">Brand *</Label>
                    <AutoSuggest
                      options={deviceBrands}
                      value={formData.device_brand}
                      onChange={(value) => setFormData({ ...formData, device_brand: value })}
                      placeholder="Search brand..."
                      testId="device-brand-select"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="device_model">Model *</Label>
                    <Input
                      id="device_model"
                      value={formData.device_model}
                      onChange={(e) => setFormData({ ...formData, device_model: e.target.value })}
                      placeholder="e.g., iPhone 15 Pro"
                      required
                      data-testid="device-model-input"
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="device_imei">IMEI Number *</Label>
                    <Input
                      id="device_imei"
                      value={formData.device_imei}
                      onChange={(e) => setFormData({ ...formData, device_imei: e.target.value.replace(/\D/g, '').slice(0, 15) })}
                      placeholder="15 digit IMEI"
                      required
                      maxLength={15}
                      data-testid="device-imei-input"
                      className="h-11"
                    />
                    <p className="text-xs text-slate-500">{formData.device_imei.length}/15 digits</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="device_price">Device Price (INR) *</Label>
                    <Input
                      id="device_price"
                      type="number"
                      min="0"
                      value={formData.device_price}
                      onChange={(e) => setFormData({ ...formData, device_price: e.target.value })}
                      placeholder="e.g., 50000"
                      required
                      data-testid="device-price-input"
                      className="h-11"
                    />
                  </div>
                </div>
              </div>

              {/* Attachments */}
              <div className="pt-4 border-t">
                <h4 className="text-sm font-medium text-slate-700 mb-3">Invoice / Receipt Attachments (Optional)</h4>
                <div className="space-y-3">
                  <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-[#005DE8] transition-colors" data-testid="file-upload-label">
                    <input type="file" accept="image/*,application/pdf" multiple onChange={handleFileUpload} className="hidden" data-testid="file-upload-input" />
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4 rotate-90" />}
                    <span className="text-sm text-slate-600">{uploading ? 'Uploading...' : 'Click to upload images or PDFs (auto-compressed)'}</span>
                  </label>
                  {attachments.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {attachments.map((att, i) => (
                        <div key={att.id} className="flex items-center gap-2 bg-slate-100 px-3 py-1.5 rounded-lg text-xs">
                          <span className="text-slate-700">{att.filename}</span>
                          <span className="text-slate-400">{(att.size / 1024).toFixed(0)}KB</span>
                          <button type="button" onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600">&times;</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="pt-4 flex justify-end">
                <Button
                  onClick={handleNextStep}
                  className="bg-[#005DE8] hover:bg-[#0047B3]"
                  data-testid="next-step-button"
                >
                  Next: Warranty
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Warranty */}
        {step === 2 && (
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                <ShieldCheck className="h-5 w-5 text-[#005DE8]" />
                Warranty Selection
              </CardTitle>
              <CardDescription>
                {isExclusive 
                  ? 'Select a warranty plan for this sale (required for exclusive contract)'
                  : 'Choose whether to attach a warranty to this sale'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Summary */}
              <div className="bg-slate-50 rounded-xl p-4">
                <h4 className="text-sm font-medium text-slate-700 mb-2">Sale Summary</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span className="text-slate-500">Customer:</span>
                  <span className="font-medium">{formData.customer_name}</span>
                  <span className="text-slate-500">Device:</span>
                  <span className="font-medium">{formData.device_brand} {formData.device_model}</span>
                  <span className="text-slate-500">Price:</span>
                  <span className="font-medium">{formatCurrency(parseFloat(formData.device_price) || 0)}</span>
                </div>
              </div>

              {/* Prohibited Device Warning */}
              {prohibitedWarning && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3" data-testid="prohibited-warning">
                  <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-900">Warranty Prohibited for this Device</p>
                    <p className="text-sm text-red-700">
                      {prohibitedWarning.match} cannot have warranty attached. Reason: {prohibitedWarning.reason}
                    </p>
                    <p className="text-sm text-red-600 mt-1">You can still complete the sale without warranty.</p>
                  </div>
                </div>
              )}

              {/* Warranty Toggle (only for non-exclusive) */}
              {!isExclusive && (
                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl border border-blue-100">
                  <div className="flex items-center gap-3">
                    <ShieldCheck className="h-6 w-6 text-[#005DE8]" />
                    <div>
                      <p className="font-medium text-slate-900">Attach Warranty?</p>
                      <p className="text-sm text-slate-600">Add extended protection for this device</p>
                    </div>
                  </div>
                  <Switch
                    checked={formData.warranty_attached}
                    onCheckedChange={(checked) => setFormData({ ...formData, warranty_attached: checked, warranty_plan_id: '' })}
                    data-testid="warranty-toggle"
                  />
                </div>
              )}

              {/* Warranty Plans */}
              {(isExclusive || formData.warranty_attached) && (
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-3">
                    {eligiblePlans.length > 0 
                      ? 'Available Warranty Plans' 
                      : 'No plans available for this device price'}
                  </h4>
                  {eligiblePlans.length > 0 ? (
                    <div className="grid gap-3">
                      {eligiblePlans.map((plan) => (
                        <div
                          key={plan.id}
                          onClick={() => setFormData({ ...formData, warranty_plan_id: plan.id, warranty_attached: true })}
                          className={cn(
                            "p-4 rounded-xl border-2 cursor-pointer transition-all",
                            formData.warranty_plan_id === plan.id
                              ? "border-[#005DE8] bg-blue-50"
                              : "border-slate-200 hover:border-slate-300"
                          )}
                          data-testid={`warranty-plan-${plan.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-slate-900">{plan.plan_name}</p>
                              <p className="text-sm text-slate-500">{plan.duration_months} months coverage</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xl font-bold text-[#005DE8]">{formatCurrency(plan.warranty_price)}</p>
                              {formData.warranty_plan_id === plan.id && (
                                <Badge className="bg-[#005DE8] text-white mt-1">Selected</Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-xl">
                      <ShieldCheck className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                      <p>No warranty plans available for this device price range</p>
                    </div>
                  )}
                  {formData.warranty_plan_id && (
                    <div className="mt-3">
                      <Label className="text-sm text-slate-600">Sell at custom price (optional)</Label>
                      <Input
                        type="number"
                        placeholder={`Base: ${formatCurrency(eligiblePlans.find(p => p.id === formData.warranty_plan_id)?.warranty_price || 0)}`}
                        value={formData.warranty_price_override}
                        onChange={(e) => setFormData({ ...formData, warranty_price_override: e.target.value })}
                        className="mt-1"
                        data-testid="warranty-price-override"
                        min={eligiblePlans.find(p => p.id === formData.warranty_plan_id)?.warranty_price || 0}
                      />
                      <p className="text-xs text-slate-400 mt-1">Leave empty to use base price. Set higher to earn profit.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="pt-4 flex justify-between">
                <Button
                  variant="outline"
                  onClick={() => setStep(1)}
                  data-testid="back-step-button"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={handleSubmit}
                  className="bg-[#005DE8] hover:bg-[#0047B3]"
                  disabled={submitting}
                  data-testid="submit-sale-button"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Recording...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Complete Sale
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Override Dialog for Exclusive Contract */}
        <Dialog open={showOverrideDialog} onOpenChange={setShowOverrideDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-600" style={{ fontFamily: 'Outfit' }}>
                <AlertTriangle className="h-5 w-5" />
                Warranty Required
              </DialogTitle>
              <DialogDescription>
                This shop has an Exclusive Warranty Contract. All sales must include a Mobikoo warranty. 
                Please select a warranty plan, or provide a reason for exemption below.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label htmlFor="override_reason">Exemption Reason (minimum 20 characters)</Label>
              <Textarea
                id="override_reason"
                value={formData.warranty_override_reason}
                onChange={(e) => setFormData({ ...formData, warranty_override_reason: e.target.value })}
                placeholder="Please explain why this sale cannot include a warranty..."
                rows={4}
                className="mt-2"
                data-testid="override-reason-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                {formData.warranty_override_reason.length}/20 characters minimum
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowOverrideDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleOverrideSubmit}
                className="bg-amber-600 hover:bg-amber-700"
                disabled={formData.warranty_override_reason.length < 20}
                data-testid="override-submit-button"
              >
                Submit Without Warranty
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
